import { FC, useState } from 'react'
import { ChevronDown, Check, Loader2, AlertCircle, Wrench, CheckCircle2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { Badge } from '../../ui/badge'
import type { AgentToolCall } from '../../../contexts/ChatContext'

interface ToolDetailRowProps {
  tool: AgentToolCall
}

const ToolDetailRow: FC<ToolDetailRowProps> = ({ tool }) => {
  const [showDetail, setShowDetail] = useState(false)
  const hasDetail = tool.input || tool.output
  const isError = tool.success === false
  const showBadge = tool.citationCount !== undefined && tool.citationCount > 0 && !tool.isRunning

  const icon = tool.isRunning
    ? <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
    : isError
      ? <AlertCircle className="w-3 h-3 text-destructive" />
      : <Check className="w-3 h-3 text-emerald-400" />

  const label = tool.skillLabel || formatToolName(tool.name)

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-2 py-0.5 text-muted-foreground',
          isError && 'text-destructive',
          hasDetail && 'cursor-pointer hover:text-foreground'
        )}
        onClick={() => hasDetail && setShowDetail(prev => !prev)}
      >
        {icon}
        <span className="font-medium">{label}</span>
        {showBadge && (
          <Badge
            variant="secondary"
            className="text-[10px] h-4 px-1.5 bg-clinical-sage/10 text-clinical-sage border-0"
          >
            {tool.citationCount} found
          </Badge>
        )}
        {hasDetail && (
          <ChevronDown
            className={cn(
              'w-3 h-3 ml-auto transition-transform',
              showDetail && 'rotate-180'
            )}
          />
        )}
      </div>
      {showDetail && (
        <div className="ml-6 mt-1 space-y-1 text-muted-foreground text-[11px]">
          {tool.input && (
            <div>
              <span className="font-medium">Input: </span>
              <pre className="inline whitespace-pre-wrap break-all">
                {truncate(tool.input, 500)}
              </pre>
            </div>
          )}
          {tool.output && (
            <div>
              <span className="font-medium">Output: </span>
              <pre className="inline whitespace-pre-wrap break-all">
                {truncate(tool.output, 500)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface ToolCardProps {
  tools: AgentToolCall[]
  compact?: boolean
}

/**
 * Collapsible tool transparency card showing which tools an agent used.
 * Displays running/complete status with expandable input/output details.
 */
type PhasePill = {
  label: string
  icon: typeof Wrench
  className: string
}

const derivePhasePill = (tools: AgentToolCall[]): PhasePill => {
  if (tools.some(t => t.isRunning)) {
    return { label: 'Working', icon: Wrench, className: 'bg-clinical-amber/10 text-clinical-amber' }
  }
  if (tools.some(t => t.success === false)) {
    return { label: 'Error', icon: AlertCircle, className: 'bg-destructive/10 text-destructive' }
  }
  return { label: 'Done', icon: CheckCircle2, className: 'bg-clinical-sage/10 text-clinical-sage' }
}

export const ToolCard: FC<ToolCardProps> = ({ tools, compact = false }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!tools || tools.length === 0) return null

  const runningCount = tools.filter(t => t.isRunning).length
  const completeCount = tools.length - runningCount
  const errorCount = tools.filter(t => t.success === false).length
  const pill = derivePhasePill(tools)
  const PillIcon = pill.icon

  const completeLabel = errorCount > 0
    ? `${completeCount} tool${completeCount !== 1 ? 's' : ''} complete · ${errorCount} error${errorCount !== 1 ? 's' : ''}`
    : `${completeCount} tool${completeCount !== 1 ? 's' : ''} complete`

  return (
    <div className={cn(
      'my-2 border border-border rounded-xl bg-muted/50 text-xs overflow-hidden',
      compact && 'my-0.5'
    )}>
      <button
        onClick={() => setIsExpanded(prev => !prev)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted transition-all duration-200 rounded-xl"
      >
        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
          {runningCount > 0 && (
            <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
          )}
          {runningCount > 0
            ? `${runningCount} tool${runningCount !== 1 ? 's' : ''} running`
            : completeLabel
          }
          <span className={cn(
            'ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px]',
            pill.className
          )}>
            <PillIcon className={cn('w-2.5 h-2.5', pill.label === 'Working' && 'animate-pulse')} />
            {pill.label}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-muted-foreground transition-transform',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {isExpanded && (
        <div className="px-3 pb-2 space-y-1">
          {tools.map((tool, i) => (
            <ToolDetailRow key={tool.callId || `${tool.name}-${i}`} tool={tool} />
          ))}
        </div>
      )}
    </div>
  )
}

const truncate = (text: string, maxLen: number): string =>
  text.length > maxLen ? text.substring(0, maxLen) + '...' : text

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  search_pubmed: 'Search PubMed',
  search_rxnorm: 'Search RxNorm',
  search_clinical_trials: 'Search ClinicalTrials.gov',
  search_fda_adverse_events: 'Search FDA FAERS',
  lookup_drug_label: 'FDA Drug Label',
  search_web: 'Web Search',
  search_open_evidence: 'OpenEvidence',
  load_skill: 'Load Skill',
  get_patient_data: 'Read Patient Data',
  update_patient_data: 'Update Patient Data',
  create_artifact: 'Create Document',
  update_artifact: 'Update Document',
  read_artifact: 'Read Document',
  generate_hpi_pe: 'Generate HPI & PE',
  generate_ap: 'Generate A&P',
  save_hpi_pe: 'Save HPI & PE',
  save_ap: 'Save A&P',
  load_hpi_pe: 'Load HPI & PE',
  load_ap: 'Load A&P',
  load_chart_data: 'Load Chart Data',
  load_structured_chart: 'Load Structured Chart',
  filter_evidence: 'Filter Evidence',
  research: 'Research',
  extract_chunk: 'Extract Data',
  reconcile_meds: 'Reconcile Meds',
  review_hpi_pe: 'Review HPI & PE',
  review_ap: 'Review A&P',
  save_note: 'Save Note',
  save_chart_data: 'Save Chart Data'
}

const formatToolName = (name: string): string =>
  TOOL_DISPLAY_NAMES[name] || name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
